import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon, User, LogOut, Settings, BarChart3, ChevronDown, ShoppingCart, Heart } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import useDarkMode from '../../hooks/useDarkMode';
import { constructApiUrl } from '../../utils/apiUtils';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, toggleDarkMode] = useDarkMode();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [productCategories, setProductCategories] = useState([]);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPositions, setDropdownPositions] = useState({});
  const [userDropdownPosition, setUserDropdownPosition] = useState({});
  const headerRef = useRef(null);
  const navItemRefs = useRef({});
  const userMenuRef = useRef(null);

  // Debug logging for mobile menu (only when menu state changes)
  useEffect(() => {
    if (isMenuOpen) {
      console.log('🔍 Mobile Menu Debug:', {
        isMenuOpen,
        isScrolled,
        scrollY: window.scrollY,
        headerHeight,
        bannerVisible,
        calculatedTop: headerHeight + (bannerVisible ? 40 : 0),
        viewportHeight: window.innerHeight,
        calculatedHeight: window.innerHeight - (headerHeight + (bannerVisible ? 40 : 0))
      });
    }
  }, [isMenuOpen, isScrolled, headerHeight, bannerVisible]);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const { getCartCount } = useCart();
  const { getWishlistCount } = useWishlist();
  const { settings, getLogoUrl, loading } = useSiteSettings();

  // Fetch product categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Use the proper API service instead of direct fetch
        const response = await fetch(constructApiUrl('product-categories?status=active'));
        
        // Check if response is ok before parsing JSON
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }
        
        const data = await response.json();
        if (data.success) {
          setProductCategories(data.data.categories || []);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Set empty array as fallback
        setProductCategories([]);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        // Only log if height actually changed
        if (height !== headerHeight) {
          if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('📏 Header height updated:', height);
          setHeaderHeight(height);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateHeaderHeight);
    window.addEventListener('resize', updateDropdownPositions);
    window.addEventListener('resize', updateUserDropdownPosition);
    
    // Initial height calculation
    updateHeaderHeight();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeaderHeight);
      window.removeEventListener('resize', updateDropdownPositions);
      window.removeEventListener('resize', updateUserDropdownPosition);
    };
  }, []);

  // Check for special day banner visibility
  useEffect(() => {
    const checkBannerVisibility = () => {
      if (typeof window !== 'undefined' && window.SpecialDayBannerVisible !== undefined) {
        const newBannerVisible = window.SpecialDayBannerVisible;
        if (newBannerVisible !== bannerVisible) {
          if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🎯 Header banner visibility changed:', newBannerVisible);
          setBannerVisible(newBannerVisible);
        }
      }
    };

    checkBannerVisibility();
    
    // Check periodically for banner state changes
    const interval = setInterval(checkBannerVisibility, 100);
    
    return () => clearInterval(interval);
  }, [bannerVisible]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen) {
        // Check if click is within user menu button or dropdown portal
        const isUserMenuClick = event.target.closest('.user-menu');
        const isDropdownClick = event.target.closest('[data-user-dropdown]');
        
        if (!isUserMenuClick && !isDropdownClick) {
          setIsUserMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substr(0, 2);
  };

  const updateDropdownPositions = () => {
    const positions = {};
    navItems.forEach((item, index) => {
      if (item.dropdown && navItemRefs.current[item.name]) {
        const rect = navItemRefs.current[item.name].getBoundingClientRect();
        positions[item.name] = {
          left: rect.left + (rect.width / 2) - 112, // 112 = half of dropdown width (224px)
          top: rect.bottom + 10
        };
      }
    });
    setDropdownPositions(positions);
  };

  const updateUserDropdownPosition = () => {
    if (userMenuRef.current) {
      const rect = userMenuRef.current.getBoundingClientRect();
      setUserDropdownPosition({
        right: window.innerWidth - rect.right,
        top: rect.bottom + 10
      });
    }
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { 
      name: 'Products', 
      path: '/products',
      dropdown: productCategories.map(cat => ({
        name: cat.name,
        path: `/products/${cat.slug}`
      }))
    },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header 
      ref={headerRef}
      className={`sticky left-0 right-0 z-50 transition-all duration-500 ease-in-out transform ${
        isScrolled 
          ? 'card-3d bg-gradient-to-r from-blue-50/95 via-indigo-50/95 to-purple-50/95 dark:from-gray-900/95 dark:via-blue-900/95 dark:to-purple-900/95 backdrop-blur-xl shadow-2xl border-b border-blue-200/30 dark:border-blue-700/50 scale-100' 
          : 'bg-gradient-to-r from-blue-50/90 via-indigo-50/90 to-purple-50/90 dark:from-gray-900/90 dark:via-blue-900/90 dark:to-purple-900/90 backdrop-blur-md shadow-lg border-b border-blue-200/20 dark:border-blue-700/30 scale-100'
      }`}
      style={{
        top: bannerVisible ? '40px' : '0px',
        transition: 'top 0.3s ease'
      }}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group header-interactive">
            {!loading && getLogoUrl(isDarkMode) ? (
              <img
                src={getLogoUrl(isDarkMode)}
                alt={settings?.siteTitle || 'Niswa Info Tech'}
                className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-110 group-hover:rotate-1 hover-lift"
              />
            ) : (
              <div className={`w-10 h-10 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-1 hover-lift ${loading ? 'logo-loading' : ''}`}>
                <span className="text-white font-bold text-xl transition-transform duration-300 group-hover:scale-110">N</span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item, index) => (
              <div key={item.name} className="relative group">
                <div
                  ref={(el) => {
                    if (el) navItemRefs.current[item.name] = el;
                  }}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 ease-in-out transform hover:scale-105 card-3d cursor-pointer ${
                    location.pathname === item.path
                      ? 'bg-gradient-to-r from-blue-600 to-purple-700 text-white shadow-glow'
                      : 'text-gray-800 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:bg-white/90 dark:hover:bg-gray-800/90 border border-blue-200/30 dark:border-blue-700/30'
                  }`}
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInDown 0.6s ease-out forwards'
                  }}
                  onMouseEnter={() => {
                    setOpenDropdown(item.name);
                    updateDropdownPositions();
                  }}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <Link to={item.path} className="relative">
                    <span className="relative">
                      {item.name}
                      <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-400 dark:to-purple-400 transition-all duration-300 ${
                        location.pathname === item.path ? 'w-full' : 'group-hover:w-full'
                      }`}></span>
                    </span>
                  </Link>
                  {item.dropdown && <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />}
                </div>
              </div>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {/* Cart Icon */}
            <div className="relative">
              <Link
                to="/cart"
                className="card-3d w-12 h-12 flex items-center justify-center rounded-xl text-gray-800 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:bg-white/90 dark:hover:bg-gray-800/90 border border-blue-200/30 dark:border-blue-700/30 transition-all duration-300 ease-in-out transform hover:scale-110 hover:rotate-3 group shadow-lg"
                aria-label="Shopping cart"
              >
                <ShoppingCart className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
              </Link>
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold notification-badge shadow-lg border-2 border-white dark:border-gray-800 z-10">
                  {getCartCount() > 99 ? '99+' : getCartCount()}
                </span>
              )}
            </div>

            {/* Wishlist Icon */}
            <div className="relative">
              <Link
                to="/wishlist"
                className="card-3d w-12 h-12 flex items-center justify-center rounded-xl text-gray-800 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:bg-white/90 dark:hover:bg-gray-800/90 border border-blue-200/30 dark:border-blue-700/30 transition-all duration-300 ease-in-out transform hover:scale-110 hover:-rotate-3 group shadow-lg"
                aria-label="Wishlist"
              >
                <Heart className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
              </Link>
              {getWishlistCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold notification-badge shadow-lg border-2 border-white dark:border-gray-800 z-10">
                  {getWishlistCount() > 99 ? '99+' : getWishlistCount()}
                </span>
              )}
            </div>

            {/* Dark Mode Toggle - Only show if enabled in admin settings */}
            {settings?.enableDarkMode && (
              <button
                onClick={toggleDarkMode}
                className="card-3d p-3 rounded-xl text-gray-800 dark:text-gray-200 hover:text-yellow-600 dark:hover:text-yellow-400 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:bg-white/90 dark:hover:bg-gray-800/90 border border-blue-200/30 dark:border-blue-700/30 transition-all duration-300 ease-in-out transform hover:scale-110 hover:rotate-12 shadow-lg"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            )}

            {/* Authentication-aware Navigation */}
            <div className="hidden md:flex items-center space-x-3">
              <Link
                to="/quote"
                className="card-3d bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl shadow-lg"
              >
                Get Quote
              </Link>
              
              {isAuthenticated ? (
                <div className="relative user-menu">
                  <button
                    ref={userMenuRef}
                    onClick={() => {
                      setIsUserMenuOpen(!isUserMenuOpen);
                      if (!isUserMenuOpen) {
                        updateUserDropdownPosition();
                      }
                    }}
                    className="flex items-center space-x-3 card-3d p-3 rounded-xl text-gray-800 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:bg-white/90 dark:hover:bg-gray-800/90 border border-blue-200/30 dark:border-blue-700/30 transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg"
                  >
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border-2 border-white/20 dark:border-gray-700/50"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <span className="text-white text-sm font-medium">
                          {getInitials(user?.name || 'User')}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-semibold">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 transition-transform duration-300" style={{ transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  </button>
                  
                  {/* User Dropdown Menu - Removed absolute positioning, will be handled by portal */}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="card-3d bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl shadow-lg"
                >
                  Login
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden card-3d p-3 rounded-xl text-gray-800 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md hover:bg-white/90 dark:hover:bg-gray-800/90 border border-blue-200/30 dark:border-blue-700/30 transition-all duration-300 ease-in-out transform hover:scale-110 shadow-lg"
              aria-label="Toggle menu"
            >
              <div className="relative">
                {isMenuOpen ? (
                  <X className="w-6 h-6 transition-all duration-300 ease-in-out transform rotate-180" />
                ) : (
                  <Menu className="w-6 h-6 transition-all duration-300 ease-in-out" />
                )}
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Portal */}
      {isMenuOpen && createPortal(
        <>
          {/* Backdrop - Only covers the menu area */}
          <div 
            className="lg:hidden fixed bg-black/50 backdrop-blur-sm z-[9998]"
            style={{ 
              top: `${headerHeight + (bannerVisible ? 40 : 0)}px`,
              left: 0,
              right: 0,
              bottom: 0
            }}
            onClick={() => setIsMenuOpen(false)}
          />
          {/* Menu Content */}
          <div 
            className="lg:hidden fixed left-0 right-0 bottom-0 card-3d bg-gradient-to-b from-blue-50/95 via-indigo-50/95 to-purple-50/95 dark:from-gray-900/95 dark:via-blue-900/95 dark:to-purple-900/95 backdrop-blur-xl shadow-2xl z-[9999] rounded-t-3xl"
            style={{ 
              top: `${headerHeight + (bannerVisible ? 40 : 0)}px`,
              maxHeight: `calc(100vh - ${headerHeight + (bannerVisible ? 40 : 0)}px)`,
              overflowY: 'auto'
            }}
          >
            <div className="px-4 pt-6 pb-6 space-y-2">
              {navItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.path}
                    className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-blue-600 to-purple-700 text-white shadow-lg'
                        : 'text-gray-800 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-white/70 dark:hover:bg-gray-800/70 backdrop-blur-md'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.dropdown && (
                    <div className="pl-6 space-y-1 mt-2">
                      {item.dropdown.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.path}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-white/50 dark:hover:bg-gray-800/50 rounded-lg transition-all duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 space-y-3">
                <Link
                  to="/quote"
                  className="block w-full text-center card-3d bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Quote
                </Link>
                <Link
                  to="/login"
                  className="block w-full text-center card-3d bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Desktop Dropdown Portals */}
      {navItems.map((item) => 
        item.dropdown && createPortal(
          <div 
            key={`dropdown-${item.name}`}
            className={`hidden lg:block fixed w-56 card-3d bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/50 transition-all duration-300 ease-in-out transform origin-top z-[9999] ${
              openDropdown === item.name ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95'
            }`}
            style={{
              top: dropdownPositions[item.name]?.top || `${headerHeight + (bannerVisible ? 40 : 0) + 20}px`,
              left: dropdownPositions[item.name]?.left || '20px',
              pointerEvents: openDropdown === item.name ? 'auto' : 'none'
            }}
            onMouseEnter={() => setOpenDropdown(item.name)}
            onMouseLeave={() => setOpenDropdown(null)}
          >
            <div className="p-3">
              {item.dropdown.map((dropdownItem, dropdownIndex) => (
                <Link
                  key={dropdownItem.name}
                  to={dropdownItem.path}
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl transition-all duration-200 ease-in-out transform hover:translate-x-2 hover:scale-105"
                  style={{
                    animationDelay: `${dropdownIndex * 50}ms`,
                    animation: 'slideInLeft 0.3s ease-out forwards'
                  }}
                >
                  {dropdownItem.name}
                </Link>
              ))}
            </div>
          </div>,
          document.body
        )
      )}

      {/* User Dropdown Portal */}
      {isUserMenuOpen && createPortal(
        <div 
          data-user-dropdown
          className="fixed w-56 card-3d bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/50 z-[9999] transition-all duration-300 ease-in-out transform origin-top"
          style={{
            top: userDropdownPosition.top || `${headerHeight + (bannerVisible ? 40 : 0) + 20}px`,
            right: userDropdownPosition.right || '2rem',
            transform: 'translateY(0)',
            pointerEvents: 'auto'
          }}
        >
          <div className="p-3">
            <Link
              to="/dashboard"
              className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl transition-all duration-200"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <User className="w-4 h-4 mr-3" />
              Dashboard
            </Link>
            <Link
              to="/profile"
              className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl transition-all duration-200"
              onClick={() => setIsUserMenuOpen(false)}
            >
              <Settings className="w-4 h-4 mr-3" />
              Profile
            </Link>
            {isAdmin() && (
              <Link
                to="/admin"
                className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl transition-all duration-200"
                onClick={() => setIsUserMenuOpen(false)}
              >
                <BarChart3 className="w-4 h-4 mr-3" />
                Admin Panel
              </Link>
            )}
            <hr className="my-3 border-gray-200 dark:border-gray-700" />
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 dark:hover:from-red-900/20 dark:hover:to-pink-900/20 rounded-xl transition-all duration-200"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Logout
            </button>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Header;
